// External Libraries
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";

// Local Components
import S from "./ProfileDetailsTab.module.scss";

// Constants
import { FIELD_TYPES } from "../../../constants/profileDetailsFields";

export function ProfileField({ field, formData, isEditing, handleChange }) {
  const { label, key, type, options, locked, disabled } = field;
  const value = formData[key];
  const otherKey = `${key}Other`;
  const otherValue = formData[otherKey];

  const isOtherSelected = () => {
    if (type === FIELD_TYPES.DROPDOWN) return value === "other";
    if (type === FIELD_TYPES.MULTISELECT) return (value || []).includes("other");
    return false;
  };

  const LabelElement = isEditing && !locked ? "label" : "div";
  const labelProps = isEditing && !locked ? { htmlFor: key } : {};

  const hasValidOtherValue = () => otherValue?.trim().length > 0;

  const shouldShowOtherInput = isOtherSelected();
  const isOtherInputInvalid = shouldShowOtherInput && !hasValidOtherValue();

  const renderEditableField = () => {
    if (type === FIELD_TYPES.TEXT) {
      return locked ? (
        <div className={S.profileDetailText}>
          <i className="pi pi-lock" title="This field is locked and cannot be edited" />
          <span className="ml-2">{value}</span>
        </div>
      ) : (
        <div className={S.profileInput}>
          <InputText
            id={key}
            className="w-full"
            value={value}
            onChange={e => handleChange(key, e.target.value)}
          />
        </div>
      );
    }

    if (type === FIELD_TYPES.DROPDOWN) {
      return locked ? (
        <div className={S.profileDetailText}>
          <i className="pi pi-lock" title="This field is locked and cannot be edited" />
          <span className="ml-2">{options?.find(opt => opt.value === value)?.label || value}</span>
        </div>
      ) : (
        <div className={S.profileInput}>
          <Dropdown
            inputId={key}
            className="w-full"
            value={value}
            options={options || []}
            onChange={e => handleChange(key, e.value)}
            placeholder={`Select ${label.toLowerCase()}`}
            disabled={disabled}
            pt={{
              select: { "aria-label": `Select ${label}` },
            }}
          />

          {shouldShowOtherInput && (
            <div className="mt-2">
              <InputText
                id={otherKey}
                className="w-full"
                value={otherValue || ""}
                onChange={e => handleChange(otherKey, e.target.value)}
                placeholder={`Please specify your ${label.toLowerCase()}`}
              />
              {isOtherInputInvalid && <small className="text-error">This field is required</small>}
            </div>
          )}
        </div>
      );
    }

    if (type === FIELD_TYPES.MULTISELECT) {
      return locked ? (
        <div className={S.profileDetailText}>
          <i className="pi pi-lock" title="This field is locked and cannot be edited" />
          <span className="ml-2">
            {(value || []).map(v => options?.find(opt => opt.value === v)?.label || v).join(", ")}
          </span>
        </div>
      ) : (
        <div className={S.profileInput}>
          <MultiSelect
            inputId={key}
            className="w-full"
            value={(value || []).map(v => v.toLowerCase())}
            options={options || []}
            onChange={e => handleChange(key, e.value)}
            placeholder={`Enter ${label.toLowerCase()}`}
            disabled={disabled}
          />

          {shouldShowOtherInput && (
            <div className="mt-2">
              <InputText
                id={otherKey}
                className="w-full"
                value={otherValue || ""}
                onChange={e => handleChange(otherKey, e.target.value)}
                placeholder={`Please specify your ${label.toLowerCase()}`}
              />
              {isOtherInputInvalid && <small className="text-error">This field is required</small>}
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  const renderReadOnlyField = () => {
    // --- DROPDOWN "Other" (Language) ---
    if (type === FIELD_TYPES.DROPDOWN && value === "other") {
      const otherValue = formData[`${key}Other`];
      return <div className={S.profileDetailText}>{otherValue || "-"}</div>;
    }

    // --- MULTISELECT (Tooling) ---
    if (Array.isArray(value)) {
      const items = value.map(v => {
        // If the multiselect item is "other", show the custom value
        if (v === "other") {
          return formData[`${key}Other`] || "other";
        }

        // Otherwise show the label or raw value
        return options?.find(opt => opt.value === v)?.label || v;
      });

      return <div className={S.profileDetailText}>{items.join(", ") || "-"}</div>;
    }

    // --- DEFAULT ---
    return (
      <div className={S.profileDetailText}>
        {options?.find(opt => opt.value === value)?.label || value || "-"}
      </div>
    );
  };

  return (
    <div>
      <LabelElement {...labelProps} className="text-muted-aaa body-sm">
        {label}
      </LabelElement>
      {isEditing ? renderEditableField() : renderReadOnlyField()}
    </div>
  );
}
